/**
 * [TD-47.6a-ui Gap 47.6a-D] PlayerStatsTab - pure presentation.
 *
 * Renders the five-stat summary line per position
 * (PPG | ADP Rank | Bye | Key1 | Key2) plus a below-fold detail grid.
 *
 * Six position variants (locked at spec v1.0):
 *   QB  → Key1: Pass TDs        | Key2: Pass Yds
 *   RB  → Key1: Rush TDs        | Key2: Total TDs (rush + rec)
 *   WR  → Key1: Receptions      | Key2: Rec TDs
 *   TE  → Key1: Receptions      | Key2: Rec TDs
 *   K   → Key1: FGM             | Key2: FG %
 *   DEF → Key1: Sacks           | Key2: Def INTs
 *
 * Handles three data states (Gap G.3):
 *   • data_availability === "available"      → render normally
 *   • data_availability === "fallback_to_2024" → render with "Showing 2024 data" pill
 *   • data_availability === "no_data_for_season" → render empty state with header preserved
 */
import { useState } from "react";

const fmt = (v, dash = "-") => (v === null || v === undefined ? dash : v);
const fmtPct = (v) => (v === null || v === undefined ? "-" : `${Math.round(v * 1000) / 10}%`);

// [TD-47.6a-ui v1.2 Redline 5 + v1.3 Redline 1] Sleeper CDN photo with
// silhouette fallback. v1.3 upsizes to 128px rounded-2xl to match the
// CompactActionPanel focal-point treatment.
function PlayerStatsPhoto({ url }) {
  const [errored, setErrored] = useState(false);
  if (!url || errored) {
    return (
      <div
        className="w-32 h-32 rounded-2xl bg-[#252525] border border-[#333] flex items-center justify-center shrink-0"
        data-testid="player-stats-photo-fallback"
      >
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      </div>
    );
  }
  return (
    <img
      src={url}
      alt="Player"
      onError={() => setErrored(true)}
      className="w-32 h-32 rounded-2xl object-cover border border-[#333] shrink-0 bg-[#252525]"
      data-testid="player-stats-photo"
    />
  );
}

function _summaryForPosition(metrics, scoringFormat) {
  const ppg = metrics?.[`pts_per_game_${scoringFormat}`];
  const adpRank = metrics?.[`adp_rank_${scoringFormat}`];
  const pos = metrics ? null : null; // pos comes from playerInfo upstream
  return { ppg, adpRank };
}

function _keysFor(position, metrics) {
  if (!metrics) return { k1Label: "-", k1Val: "-", k2Label: "-", k2Val: "-" };
  switch (position) {
    case "QB":
      return {
        k1Label: "Pass TDs", k1Val: fmt(metrics.pass_tds),
        k2Label: "Pass Yds", k2Val: fmt(metrics.pass_yards),
      };
    case "RB": {
      const totalTDs = (metrics.rush_tds ?? 0) + (metrics.rec_tds ?? 0);
      return {
        k1Label: "Rush TDs", k1Val: fmt(metrics.rush_tds),
        k2Label: "Total TDs", k2Val: totalTDs,
      };
    }
    case "WR":
    case "TE":
      return {
        k1Label: "Receptions", k1Val: fmt(metrics.receptions),
        k2Label: "Rec TDs", k2Val: fmt(metrics.rec_tds),
      };
    case "K":
      return {
        k1Label: "FGM", k1Val: fmt(metrics.fgm),
        k2Label: "FG %", k2Val: fmtPct(metrics.fg_pct),
      };
    case "DEF":
      return {
        k1Label: "Sacks", k1Val: fmt(metrics.sacks),
        k2Label: "Def INTs", k2Val: fmt(metrics.def_ints),
      };
    default:
      return { k1Label: "-", k1Val: "-", k2Label: "-", k2Val: "-" };
  }
}

function _belowFoldFor(position, metrics) {
  if (!metrics) return [];
  switch (position) {
    case "QB":
      return [
        ["Completion %", metrics.pass_attempts ? fmtPct((metrics.completions ?? 0) / metrics.pass_attempts) : "-"],
        ["INT", fmt(metrics.interceptions)],
        ["Rush Yds", fmt(metrics.rush_yards)],
        ["Rush TDs", fmt(metrics.rush_tds)],
        ["QB Rating", fmt(metrics.qb_rating)],
      ];
    case "RB":
      return [
        ["Rush Att", fmt(metrics.rush_attempts)],
        ["YPC", fmt(metrics.yards_per_carry)],
        ["Rec Yds", fmt(metrics.rec_yards)],
        ["Rec", fmt(metrics.receptions)],
        ["Targets", fmt(metrics.targets)],
      ];
    case "WR":
      return [
        ["Targets", fmt(metrics.targets)],
        ["Rec Yds", fmt(metrics.rec_yards)],
        ["YPR", fmt(metrics.yards_per_reception)],
        ["Air Yds", fmt(metrics.air_yards)],
        ["YAC", fmt(metrics.yards_after_catch)],
      ];
    case "TE":
      return [
        ["Targets", fmt(metrics.targets)],
        ["Rec Yds", fmt(metrics.rec_yards)],
        ["YPR", fmt(metrics.yards_per_reception)],
        ["YAC", fmt(metrics.yards_after_catch)],
      ];
    case "K":
      return [
        ["FGA", fmt(metrics.fga)],
        ["XPM", fmt(metrics.xpm)],
        ["XPA", fmt(metrics.xpa)],
      ];
    case "DEF":
      return [
        ["Def TDs", fmt(metrics.def_tds)],
        ["Pts Allowed/G", fmt(metrics.points_allowed_avg)],
        ["Yds Allowed/G", fmt(metrics.yards_allowed_avg)],
      ];
    default:
      return [];
  }
}

function StatBlock({ label, value, testid, accent = "#FF6B00", featured = false }) {
  return (
    <div
      data-testid={testid}
      className={`relative flex flex-col items-center justify-center rounded-md py-2 px-1.5 border-2 overflow-hidden ${featured ? "" : ""}`}
      style={{
        background: `linear-gradient(180deg, ${accent}18 0%, ${accent}08 100%)`,
        borderColor: `${accent}55`,
      }}
    >
      {featured && (
        <span
          className="absolute top-0 left-0 right-0 h-0.5"
          style={{ background: accent }}
        />
      )}
      <p
        className="font-display font-black text-[#F5F5F5] leading-none uppercase"
        style={{ fontSize: featured ? 28 : 22 }}
      >
        {value}
      </p>
      <p className="font-display font-black text-[9px] uppercase tracking-widest text-[#A0A0A0] mt-1.5">
        {label}
      </p>
    </div>
  );
}

export default function PlayerStatsTab({
  playerInfo,
  metrics,
  seasonYear,
  dataAvailability,
  scoringFormat = "half_ppr",
}) {
  if (!playerInfo) return null;
  const { name, position, nfl_team: nflTeam, bye_week: byeWeek, photo_url: photoUrl } = playerInfo;
  const noData = dataAvailability === "no_data_for_season" || !metrics;
  const isFallback = dataAvailability === "fallback_to_2024";

  const { ppg, adpRank } = _summaryForPosition(metrics, scoringFormat);
  const { k1Label, k1Val, k2Label, k2Val } = _keysFor(position, metrics);
  const belowFold = _belowFoldFor(position, metrics);

  // [E1-CORRECTION Fix 9] Position color accent for visual hierarchy.
  const POSITION_ACCENTS = {
    QB: "#7B5BD8",  // purple
    RB: "#22C55E",  // green
    WR: "#3B82F6",  // blue
    TE: "#F59E0B",  // amber
    K:  "#06B6D4",  // cyan
    DEF: "#EF4444", // red
  };
  const accentColor = POSITION_ACCENTS[(position || "").toUpperCase()] || "#FF6B00";

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="player-stats-tab">
      {/* [E1-CORRECTION Fix 9] Position-colored top accent bar. */}
      <div
        className="h-1 shrink-0"
        style={{ background: accentColor }}
        data-testid="player-stats-position-accent"
      />
      {/* Header - player identity (always rendered, even in no-data state) */}
      <div className="px-4 py-3 border-b border-[#252525] shrink-0">
        <div className="flex items-center gap-3">
          {/* [TD-47.6a-ui v1.1 Redline 5] Sleeper CDN photo with silhouette fallback. */}
          <PlayerStatsPhoto url={photoUrl} />
          <div className="min-w-0 flex-1 flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <h2
                className="font-display font-black text-xl text-[#F5F5F5] truncate"
                data-testid="player-stats-name"
              >
                {name}
              </h2>
              <p className="font-body text-xs text-[#A0A0A0] mt-0.5">
                <span
                  className="font-display font-bold inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider"
                  style={{ background: `${accentColor}22`, color: accentColor }}
                  data-testid="player-stats-position"
                >
                  {position}
                </span>
                {nflTeam ? <> · <span data-testid="player-stats-team">{nflTeam}</span></> : null}
                {byeWeek ? <> · Bye <span data-testid="player-stats-bye">{byeWeek}</span></> : null}
              </p>
            </div>
            {isFallback ? (
              <span
                className="font-display font-bold text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#FF6B00]/20 text-[#FF6B00] shrink-0"
                data-testid="player-stats-fallback-pill"
              >
                Showing 2024 data
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {noData ? (
          <div
            className="h-full flex flex-col items-center justify-center text-center"
            data-testid="player-stats-no-data"
          >
            <p className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0]">
              No data available
            </p>
            <p className="font-body text-xs text-[#666] mt-2 max-w-xs">
              {position === "DEF"
                ? "No defensive stats recorded yet."
                : "This player has no recorded stats for 2024 or 2025."}
            </p>
          </div>
        ) : (
          <>
            {/* Five-stat summary line - featured stadium-card treatment */}
            <div
              className="grid grid-cols-5 gap-2 pb-4 border-b border-[#252525]"
              data-testid="player-stats-summary-line"
            >
              <StatBlock
                label={`PPG ${scoringFormat === "half_ppr" ? "0.5" : scoringFormat === "full_ppr" ? "1.0" : "STD"}`}
                value={fmt(ppg)}
                testid="player-stats-ppg"
                accent={accentColor}
                featured
              />
              <StatBlock label="ADP Rank" value={adpRank ? `${position}${adpRank}` : "-"} testid="player-stats-adp-rank" accent={accentColor} />
              <StatBlock label="Bye" value={fmt(byeWeek)} testid="player-stats-summary-bye" accent={accentColor} />
              <StatBlock label={k1Label} value={k1Val} testid="player-stats-key1" accent={accentColor} />
              <StatBlock label={k2Label} value={k2Val} testid="player-stats-key2" accent={accentColor} />
            </div>

            {/* Below-fold detail grid - boxed cards with accent border */}
            {belowFold.length > 0 ? (
              <div
                className="grid grid-cols-3 gap-2 pt-4"
                data-testid="player-stats-below-fold"
              >
                {belowFold.map(([label, value]) => (
                  <div
                    key={label}
                    data-testid={`player-stats-detail-${label.toLowerCase().replace(/\s|%|\//g, "-")}`}
                    className="rounded-md px-2.5 py-1.5 border bg-[#0F0F12]"
                    style={{ borderColor: `${accentColor}33` }}
                  >
                    <p className="font-display font-black text-[9px] uppercase tracking-widest text-[#A0A0A0]">
                      {label}
                    </p>
                    <p className="font-display font-black text-base text-[#F5F5F5] leading-tight mt-0.5">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}

            <p className="font-body text-[10px] text-[#666] mt-6">
              {seasonYear} season · {metrics?.games_played ?? 0} games played
            </p>

            {/* [v1.4 spruce-up] Single subtle upgrade nudge - not nagging,
                positioned at the very bottom of the body so free-tier users
                see WHAT they get with PGF Analytics without feeling held hostage. */}
            <div
              data-testid="player-stats-upgrade-nudge"
              className="mt-4 mx-auto max-w-sm rounded-lg border border-[#FF6B00]/40 bg-gradient-to-r from-[#FF6B00]/[0.06] to-transparent px-3 py-2.5 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <p className="font-display font-black text-[10px] uppercase tracking-widest text-[#FF6B00]">
                  PGF Analytics - unlock the full read
                </p>
                <p className="font-body text-[11px] text-[#F5F5F5] mt-0.5 leading-snug">
                  Five-axis PGF Rank radar · gold/silver value alerts · live Bid Confidence slider
                </p>
              </div>
              <button
                type="button"
                data-testid="player-stats-upgrade-cta"
                onClick={() => { window.location.href = "/pricing"; }}
                className="shrink-0 px-3 py-1.5 rounded bg-[#FF6B00] hover:bg-[#FF8533] font-display font-black text-[10px] uppercase tracking-wider text-white whitespace-nowrap transition-colors"
              >
                $4.99 / draft
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
